import { Table } from "antd";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { LoadingOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getColumns } from './config';
import { useModule } from "../../../../../../../helpers/user";
import { StakeholderService } from "../../../../../../../services";
import SupplyChainLocationService from "../../../../../../../services/SupplyChainLocationService";
import TablePagination from "../../../../../../Table/pagination";
import { getLocales } from "../../../../../../Table/newTable";

export const Style = styled.div`
  overflow: auto;
  height: 333px;
  display: flex;
  flex-direction: column;

  .default-pagination {
    display: flex;
    gap: 1rem;
    border-top: 1px solid #F2F4F7;

    .pages {
      font-size: 12px;
      font-weight: 500;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .buttons {
      gap: 0.75rem;
      display: flex;
      flex: 1;
      justify-content: flex-end;

      .ant-btn {
        border-radius: 8px;
      }
    }
  }

  .ant-table-wrapper {
    flex: 1;

    .ant-table-tbody > tr.ant-table-row-selected > td {
      background: white;
    }

    td.ant-table-column-sort {
      background: white;
    }
  }
`;

const PER_PAGE = 200;

export default function List({
  subject = 'locations',
  setNoData,
  filter,
  onAdd = () => { },
  onRemove = () => { },
  items = [],
}) {
  const { t } = useTranslation();
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [dataSource, setDataSource] = useState([]);
  const { mod } = useModule();
  const [options, setOptions] = useState({});
  const [sorter, setSorter] = useState();
  const [initDone, setInitDone] = useState(false);

  const selectedKeys = useMemo(() => items.map((it) => it.id), [items]);

  const _onChangeSelected = (val) => {
    const isUnselect = !!items.find((i) => i.id === val.id);

    if (isUnselect) {
      onRemove(val.id);
    } else {
      onAdd({ id: val.id, name: val.name });
    }
  };

  const fetchData = useCallback(async (_page) => {
    setLoading(true);

    const res = subject === 'locations' ? await SupplyChainLocationService.getAll({
      module: mod,
      project: localStorage.getItem('project'),
      sourceId: 'overview',
      pagination: { page: _page, size: PER_PAGE },
      search: filter ? {
        qs: filter,
        fields: ["datastakeId", "locationName"],
      } : undefined,
      sort: sorter ? {
        dir: sorter.order,
        by: sorter.key,
      } : undefined,
    }) : await StakeholderService.get({
      module: mod,
      view: 'scp',
      project: localStorage.getItem('project'),
      sourceId: 'overview',
      pagination: { page: _page, size: PER_PAGE },
      search: filter ? {
        qs: filter,
        fields: ["datastakeId", "name"],
      } : undefined,
      sort: sorter ? {
        dir: sorter.order,
        by: sorter.key,
      } : undefined,
    });

    if (res.data) {
      setTotal(res.data.totalItems)

      if (subject === 'locations') {
        setOptions({
          category: res.data.options?.typeOfLocationOptions || [],
        })
      } else {
        setOptions({
          category: res.data.options?.categoryOptions || [],
          subCategory: res.data.options?.subCategoryOptions || [],
        })
      }

      if (res.data.totalItems) {
        setDataSource(res.data.data.map((d) => ({
          name: subject === 'locations' ? d.locationName : d.name,
          id: d.id,
          key: d.id,
          type: d.category,
          subCategory: d.subCategory,
        })))
      } else if (!filter) {
        setNoData(true);
      }
    }

    setLoading(false);
  }, [filter, sorter]);

  useEffect(() => {
    setDataSource([]);
    fetchData(page);
    if (!initDone) {
      setInitDone(true);
    }
  }, [page]);

  useEffect(() => {
    setPage(1);
    if (page === 1 && initDone) {
      setDataSource([]);
      fetchData(page);
    }
  }, [filter, sorter]);

  const canGoNext = useMemo(() => {
    const _total = total;

    if (_total === 0) {
      return false;
    }

    const pages = Math.ceil(_total / PER_PAGE);

    if (page !== pages) {
      return true;
    }

    return false;
  }, [page, total, items]);

  const canGoPrev = useMemo(() => page !== 1, [page]);

  const goNext = useCallback(() => {
    if (loading || !canGoNext) {
      return;
    }

    setPage(page + 1);
  }, [page, loading, canGoNext]);

  const goPrev = useCallback(() => {
    if (loading || !canGoPrev) {
      return;
    }

    setPage(page - 1);
  }, [page, loading, canGoPrev]);

  const columns = useMemo(() => (
    getColumns({ t, options, sorter, view: subject })
  ), [t, options, sorter, subject]);

  const onTableChange = (page, f, sorter) => {
    const { column, order } = sorter || {};
    if (column && order) {
      setSorter({ order, key: column.key });
    } else {
      setSorter(undefined);
    }
  };

  return (
    <Style>
      <div className="daf-table-wrapper flex-1 flex-c extra-small small-body">
        {loading ? (
          <div className="flex-1 flex-c jus-c no-border">
            <LoadingOutlined />
          </div>
        ) : (
          <Table
            locale={getLocales(t)}
            size="small"
            columns={columns}
            dataSource={dataSource}
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onSelect: _onChangeSelected,
              hideSelectAll: true,
            }}
            scroll={{ y: 210 }}
            pagination={false}
            onChange={onTableChange}
          />
        )}
        <TablePagination
          t={t}
          page={page}
          totalPages={total}
          goPrev={goPrev}
          goNext={goNext}
          canGoNext={canGoNext}
          canGoPrev={canGoPrev}
          totalItems={total}
          doTotalItems
          perPage={PER_PAGE}
          arrowIcons
        />
      </div>
    </Style>
  )
}
